import { useMemo } from "react";

import { Table, ConfigProvider, Tooltip, Flex, Tag } from "antd";

import EditFieldValue from "./EditFieldValue";
import ObjectFieldValue from "@/components/views/ObjectFieldValue";
import CheckCircleIcon from "@/icons/CheckCircleIcon";
import ExclamationIcon from "@/icons/ExclamationIcon";

// Таблица полей объекта
const ObjectFieldsTable = ({
  fields = [],
  object = {},
  subObjects = [],
  definition_uuid,
  editable = false,
  onFieldChange = null,
  units = [],
  unit_uuid = null,
  theme = null,
  variant = null,
  isAdmins = false,
  onScreenshot,
  color,
  onRefresh,
  onRelatedTasksRefresh,
  onMaskValidate,
  nspdChangedFields = [],
  nspdFailedFields = [],
  nspdCadastralFieldUuid = null,
}) => {
  const isPassport = theme === "passport";

  const hasNotLink = variant === "not-link";

  const changedSet = useMemo(
    () => new Set(nspdChangedFields),
    [nspdChangedFields],
  );

  const failedMap = useMemo(
    () =>
      new Map(nspdFailedFields.map((item) => [item.field_name, item.reason])),
    [nspdFailedFields],
  );

  const columns = useMemo(() => {
    return [
      {
        title: "Название поля",
        dataIndex: "name",
        width: "35%",
        render: (name, field) => {
          const nameNode = field.description ? (
            <Tooltip title={field.description} placement="topLeft">
              <span
                style={{
                  cursor: "help",
                  paddingBlock: 5,
                  display: "inline-block",
                }}
              >
                {name}
              </span>
            </Tooltip>
          ) : (
            <span style={{ paddingBlock: 5, display: "inline-block" }}>
              {name}
            </span>
          );

          return (
            <Flex align="center" gap={6} wrap>
              {nameNode}

              {field.nspd_key_uuid && field.nspd_key && (
                <Tooltip title="Поле участвует в синхронизации данных из НСПД">
                  <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                    НСПД
                  </Tag>
                </Tooltip>
              )}
              {field.isNspdCadastralField && (
                <Tooltip title="Это поле используется как кадастровый номер для синхронизации с НСПД">
                  <Tag color="purple" style={{ marginInlineEnd: 0 }}>
                    Кадастровый номер
                  </Tag>
                </Tooltip>
              )}
              {field.isNspdChanged && (
                <Tooltip title="Успешно синхронизировано">
                  <Tag color="green" style={{ marginInlineEnd: 0 }}>
                    <CheckCircleIcon />
                  </Tag>
                </Tooltip>
              )}

              {field.isNspdFailed && (
                <Tooltip title={field.nspdFailedReason}>
                  <Tag color="red" style={{ marginInlineEnd: 0 }}>
                    <ExclamationIcon />
                  </Tag>
                </Tooltip>
              )}
            </Flex>
          );
        },
        onCell: (record) => ({
          colSpan: record.type === "table" && record.value ? 0 : 1,
        }),
      },
      {
        title: "Значение поля",
        dataIndex: "value",
        render: (val, field) => {
          const isRepository = field?.type === "repository";
          const isParentPolygon = field?.type === "parent_polygon";
          const isSubObject = subObjects?.some(
            (sub) => sub?.definition?.uuid === field?.object,
          );
          return editable && !isParentPolygon ? (
            <EditFieldValue
              value={val}
              field={field}
              units={units}
              object_unit_uuid={object.unit_uuid}
              unit_uuid={unit_uuid}
              onChange={(val) => onFieldChange(field.field_name, val)}
              onValidate={(isValid) =>
                onMaskValidate?.(field.field_name, isValid)
              }
              isAdmins={isAdmins}
              color={color}
              isNew={!object.uuid}
              onRefresh={onRefresh}
              onRelatedTasksRefresh={onRelatedTasksRefresh}
              object_uuid={object.uuid}
              object_name={object.name}
              definition_uuid={definition_uuid}
              canEditObject={!object.readOnly}
            />
          ) : (
            <ObjectFieldValue
              value={val}
              field={field}
              unit_uuid={unit_uuid}
              object_unit_uuid={object.unit_uuid}
              isAnchor={isPassport}
              isSubObject={isRepository ? isSubObject : true}
              notLink={hasNotLink}
              isAdmins={isAdmins}
              color={color}
              object_uuid={object.uuid}
              object_name={object.name}
              definition_uuid={definition_uuid}
              canEditObject={!object.readOnly}
              units={units}
              onRefresh={onRefresh}
              onRelatedTasksRefresh={onRelatedTasksRefresh}
              onScreenshot={
                onScreenshot
                  ? (url) =>
                      onScreenshot({
                        field_uuid: field.uuid,
                        url,
                        object_uuid: object.uuid,
                      })
                  : undefined
              }
            />
          );
        },
        onCell: (record) => ({
          colSpan: record.type === "table" && record.value ? 2 : 1,
        }),
      },
    ];
  }, [
    editable,
    units,
    unit_uuid,
    object.uuid,
    object.name,
    object.unit_uuid,
    object.readOnly,
    isAdmins,
    color,
    definition_uuid,
    isPassport,
    hasNotLink,
    subObjects,
    onFieldChange,
    onRefresh,
    onRelatedTasksRefresh,
    onScreenshot,
    onMaskValidate,
  ]);

  const data = useMemo(() => {
    return fields
      .filter(({ type }) => !(isPassport && type === "table"))
      .map((field) => ({
        ...field,
        uuid: field.uuid,
        field: field.field,
        type: field.type,
        name: field.caption || field.name,
        description: field.description,
        value: field.field_name ? object[field.field_name] : "",
        field_name: field.field_name,
        object: field.object,
        isNspdChanged: changedSet.has(field.field_name),
        isNspdFailed: failedMap.has(field.field_name),
        nspdFailedReason: failedMap.get(field.field_name),
        nspd_key_uuid: field.nspd_key_uuid,
        nspd_key: field.nspd_key,
        isNspdCadastralField: field.field === nspdCadastralFieldUuid,
      }));
  }, [
    fields,
    object,
    isPassport,
    changedSet,
    failedMap,
    nspdCadastralFieldUuid,
  ]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            borderColor: isPassport ? "#E9E9E9" : "#f7f7f7",
            rowHoverBg: isPassport ? "#F8FBFF" : "#fcfcfc",
          },
        },
      }}
    >
      <Table
        showHeader={false}
        dataSource={data}
        columns={columns}
        rowKey="uuid"
        pagination={false}
        size="small"
        bordered={isPassport}
        className={isPassport && "striped-table"}
        tableLayout="fixed"
        onRow={(record) => ({
          style: record.isNspdChanged
            ? { background: "#f6ffed" }
            : record.isNspdFailed
              ? { background: "#fffbe6" }
              : undefined,
        })}
      />
    </ConfigProvider>
  );
};

export default ObjectFieldsTable;
